'use client';

import { useCallback, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  Badge,
  Button,
  ConfirmationDialog,
  Menu,
  MetaDefault,
} from '@terra/ui-design-system';
import { HStack, token } from '@terra/ui-styled-system';
import { Tick01Icon } from '@terra/ui-icons';
import { ArchivedActionGate } from '../common/ArchivedActionGate';
import { usePublishFinding } from '../../hooks/api/usePublishFinding';

type FindingPublicationBadgeProps = {
  findingId: string;
  isPublished: boolean;
  isArchived?: boolean;
  interactive?: boolean;
  onPublicationChange?: (findingId: string, isPublished: boolean) => void;
};

type PublicationOption = {
  value: 'published' | 'unpublished';
  label: string;
  isSelected: boolean;
};

export function FindingPublicationBadge({
  findingId,
  isPublished,
  isArchived,
  interactive,
  onPublicationChange,
}: FindingPublicationBadgeProps) {
  const t = useTranslations('designSystem.findings.editDrawer');
  const tLifecycle = useTranslations('designSystem.lifecycle');
  const { publishFinding, isPublishing } = usePublishFinding();
  const [isPublishConfirmOpen, setIsPublishConfirmOpen] = useState(false);
  const statusLabel = isPublished
    ? t('sections.publicationStatus.published')
    : t('sections.publicationStatus.unpublished');
  const options = useMemo(
    (): PublicationOption[] => [
      {
        value: 'published',
        label: t('sections.publicationStatus.published'),
        isSelected: isPublished,
      },
      {
        value: 'unpublished',
        label: t('sections.publicationStatus.unpublished'),
        isSelected: !isPublished,
      },
    ],
    [isPublished, t],
  );
  const handlePublishConfirmOpenChange = useCallback(
    ({ open }: { open: boolean }) => {
      setIsPublishConfirmOpen(open);
    },
    [],
  );
  const handleConfirmPublish = useCallback(async () => {
    const didPublish = await publishFinding({
      findingId,
      isPublished: true,
    });

    if (didPublish) {
      onPublicationChange?.(findingId, true);
      setIsPublishConfirmOpen(false);
    }
  }, [findingId, onPublicationChange, publishFinding]);
  const handleSelectOption = useCallback(
    async (option: PublicationOption) => {
      if (isPublishing || isArchived || option.isSelected) {
        return;
      }

      if (option.value === 'published') {
        setIsPublishConfirmOpen(true);

        return;
      }

      const didUnpublish = await publishFinding({
        findingId,
        isPublished: false,
      });

      if (didUnpublish) {
        onPublicationChange?.(findingId, false);
      }
    },
    [findingId, isArchived, isPublishing, onPublicationChange, publishFinding],
  );
  const badge = (
    <Badge
      variant="outline"
      size="sm"
      icon={
        isPublished ? (
          <Tick01Icon size={12} color={token('colors.green.9')} />
        ) : undefined
      }
    >
      {statusLabel}
    </Badge>
  );

  if (!interactive) {
    return badge;
  }

  return (
    <>
      <ArchivedActionGate
        isArchived={Boolean(isArchived)}
        tooltip={tLifecycle('archivedFindingActionTooltip')}
      >
        <Menu.Root
          positioning={{
            placement: 'bottom-start',
            strategy: 'fixed',
          }}
          lazyMount
          unmountOnExit
        >
          <Menu.Trigger asChild disabled={isPublishing}>
            <Button
              size="xs"
              variant="ghost"
              h="auto"
              p="0"
              minW="0"
              aria-label={t('sections.publicationStatus.title')}
              loading={isPublishing}
            >
              {badge}
            </Button>
          </Menu.Trigger>
          <Menu.Positioner>
            <Menu.Content>
              <Menu.ItemGroup>
                {options.map((option) => (
                  <Menu.Item
                    key={option.value}
                    value={option.value}
                    onClick={() => handleSelectOption(option)}
                    disabled={isPublishing}
                  >
                    <HStack gap="2" justifyContent="space-between" w="full">
                      <MetaDefault>{option.label}</MetaDefault>
                      {option.isSelected ? (
                        <Tick01Icon size={12} color={token('colors.green.9')} />
                      ) : null}
                    </HStack>
                  </Menu.Item>
                ))}
              </Menu.ItemGroup>
            </Menu.Content>
          </Menu.Positioner>
        </Menu.Root>
      </ArchivedActionGate>
      <ConfirmationDialog
        isOpen={isPublishConfirmOpen}
        onOpenChange={handlePublishConfirmOpenChange}
        title={t('publishConfirmation.title')}
        description={t('publishConfirmation.message')}
        confirmText={t('publishConfirmation.yes')}
        cancelText={t('publishConfirmation.no')}
        onConfirm={handleConfirmPublish}
        loading={isPublishing}
        variant="warning"
      />
    </>
  );
}
